// help-center-edit.component.ts
import { Component, Injector, OnInit, AfterViewChecked, ElementRef, ViewChild } from '@angular/core';
import { MessageService, ConfirmationService } from 'primeng/api';
import { finalize } from 'rxjs';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { HttpClient } from '@angular/common/http';
import { SessionBaseComponent } from 'src/app/shared/components/session.base.component';
import { HelpCenterService } from '../../../service/help-center.service';
import { HelpCenterEditParams } from '../model/HelpCenterEditParams';
import { HelpCenterResp } from '../model/HelpCenterResp';
import { HelpCenterImageService } from 'src/app/shared/service/help-center-image.service';

@Component({
  selector: 'help-center-edit',
  templateUrl: './help-center-edit.component.html',
  styleUrls: ['./help-center-edit.component.scss']
})
export class HelpCenterEditComponent extends SessionBaseComponent implements OnInit, AfterViewChecked {
  @ViewChild('viewPreviewContent', { static: false }) viewPreviewContent?: ElementRef<HTMLElement>;
  @ViewChild('dockPreviewContent', { static: false }) dockPreviewContent?: ElementRef<HTMLElement>;

  permissionCode = '';
  isIT = false;

  editData?: HelpCenterResp;
  loading = false;

  isEditing = false;

  // 初始內容 - 只用來初始化 Editor，之後不再改變
  initialContent = '';

  // 當前編輯內容 - 只接收 Editor 的輸出，不反向影響 Editor
  editContent = '';

  draftContent = '';
  releasedContent = '';

  // 兩個預覽：左側 Dialog、右側 Dock
  dialogPreviewHtml: SafeHtml = '';
  dockPreviewHtml: SafeHtml = '';

  canEdit = false;

  pageTitle = '';
  private previewNeedsImageConversion = false;

  constructor(
    private injector: Injector,
    private helpCenterService: HelpCenterService,
    private messageService: MessageService,
    private confirmService: ConfirmationService,
    private sanitizer: DomSanitizer,
    private http: HttpClient,
    private imageService: HelpCenterImageService
  ) {
    super(injector);
  }

  async ngOnInit(): Promise<void> {
    await super.ngOnInit();

    this.permissionCode = this.routeStateService.getCurrent().data.data.permissionCode;
    this.isIT = this.routeStateService.getCurrent().data.data.isIT;
    this.canEdit = this.routeStateService.getCurrent().data.data.canEdit;
    if (this.permissionCode) {
      this.loadData();
    }
  }

  ngAfterViewChecked(): void {
    if (!this.previewNeedsImageConversion) {
      return;
    }

    this.previewNeedsImageConversion = false;
    this.convertPreviewImagesToBlobs();
  }

  loadData(): void {
    this.loading = true;
    this.helpCenterService.getHelpCenterByPermissionCode(this.permissionCode)
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: (resp: HelpCenterResp) => {
          this.editData = resp;

          const draftHtml = this.isIT
            ? (resp.draftGuideContentIT || '')
            : (resp.draftGuideContent || '');

          const releasedHtml = this.isIT
            ? (resp.releasedGuideContentIT || '')
            : (resp.releasedGuideContent || '');

          // 設定初始內容和編輯內容
          const content = draftHtml || releasedHtml;
          this.initialContent = content;
          this.editContent = content;
          this.draftContent = draftHtml;
          this.releasedContent = releasedHtml;

          // 初始化兩個預覽
          this.updatePreviews(content);

          this.updatePageTitle(resp.guideHeader);
        },
        error: (err) => {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('Common.Toast.Error'),
            detail: this.translateService.instant('HelpCenter.Message.LoadDataFailed')
          });
        }
      });
  }

  updatePageTitle(guideHeader: string): void {
    const typeLabel = this.translateService.instant(
      this.isIT ? 'HelpCenter.Setting.Results.ITArticle' : 'HelpCenter.Setting.Results.UserArticle'
    );
    this.pageTitle = `${typeLabel} - ${guideHeader}`;
  }

  toggleEdit(): void {
    if (this.isEditing && this.hasUnsavedChanges()) {
      this.confirmService.confirm({
        header: this.translateService.instant('Common.Confirm.Header'),
        message: this.translateService.instant('HelpCenter.Editing.Confirm.UnsavedChanges'),
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: this.translateService.instant('Common.Button.Confirm'),
        rejectLabel: this.translateService.instant('Common.Button.Cancel'),
        accept: () => {
          this.isEditing = false;
          this.previewNeedsImageConversion = true;
          this.loadData();
        }
      });
    } else {
      this.isEditing = !this.isEditing;
      this.previewNeedsImageConversion = true;
    }
  }

  /**
   * 從 Editor 接收變更 - 單向流動，不會反向影響 Editor
   */
  onEditorChange(value: string): void {
    console.log('Editor 內容變更，長度:', value?.length);

    // 直接更新 editContent，不會觸發 Editor 的 writeValue
    this.editContent = value;

    // 只更新右側預覽
    this.updatePreviews(value);
  }

  private updatePreviews(html: string): void {
    let displayHtml = html || '<p style="color: #999;">No content</p>';
    displayHtml = displayHtml
      .replace(/\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;')
      .replace(/ {2}/g, '&nbsp;&nbsp;');

    displayHtml = this.prepareProtectedImageHtml(displayHtml);

    this.dialogPreviewHtml = this.sanitizer.bypassSecurityTrustHtml(displayHtml);
    this.dockPreviewHtml = this.sanitizer.bypassSecurityTrustHtml(displayHtml);
    this.previewNeedsImageConversion = true;
  }

  private prepareProtectedImageHtml(html: string): string {
    if (!html || !this.containsProtectedImage(html)) {
      return html;
    }

    const wrapper = document.createElement('div');
    wrapper.innerHTML = html;
    const images = wrapper.querySelectorAll('img');

    images.forEach((img: HTMLImageElement) => {
      const src = img.getAttribute('src') || '';
      if (!src || !this.isProtectedImageUrl(src)) {
        return;
      }

      if (img.getAttribute('data-blob-loading') === 'true') {
        img.removeAttribute('data-blob-loading');
      }

      img.setAttribute('data-original-src', src);
      img.setAttribute('src', 'about:blank');
    });

    return wrapper.innerHTML;
  }

  private convertPreviewImagesToBlobs(): void {
    const containers = [
      this.viewPreviewContent?.nativeElement,
      this.dockPreviewContent?.nativeElement
    ].filter((el): el is HTMLElement => !!el);

    containers.forEach((container) => {
      const images = container.querySelectorAll('img');
      images.forEach((img: HTMLImageElement) => {
        const src = img.getAttribute('src');
        const originalSrc = img.getAttribute('data-original-src');

        if (img.getAttribute('data-blob-loading') === 'true') {
          img.removeAttribute('data-blob-loading');
        }
        if (src?.startsWith('blob:')) {
          return;
        }

        const targetUrl = originalSrc || src;
        if (!targetUrl || !this.isProtectedImageUrl(targetUrl)) {
          return;
        }

        this.loadImageAsBlob(targetUrl, img);
      });
    });
  }

  private loadImageAsBlob(url: string, imgElement: HTMLImageElement): void {
    imgElement.setAttribute('data-original-src', url);
    imgElement.setAttribute('data-blob-loading', 'true');
    const requestUrl = this.resolveProtectedImageUrl(url);
    this.http.get(requestUrl, { responseType: 'blob' }).subscribe({
      next: (blob) => {
        const blobUrl = URL.createObjectURL(blob);
        imgElement.src = blobUrl;
        imgElement.removeAttribute('data-blob-loading');
      },
      error: (err) => {
        imgElement.removeAttribute('data-blob-loading');
        console.error('Failed to load preview image:', url, err);
      }
    });
  }

  private isProtectedImageUrl(url: string): boolean {
    return url.includes('/HelpCenterImage/images/') || url.includes('/guide-center/v1/images/');
  }

  private containsProtectedImage(html: string): boolean {
    return html.includes('/HelpCenterImage/images/') || html.includes('/guide-center/v1/images/');
  }

  private resolveProtectedImageUrl(url: string): string {
    return this.imageService.resolveImageRequestUrl(url);
  }
  get isDraftSaved(): boolean {
    return this.editContent === this.draftContent;
  }

  get isReleaseSaved(): boolean {
    return this.editContent === this.releasedContent;
  }

  saveDraft(): void {
    const seqNo = this.isIT ? this.editData?.itSeqNo : this.editData?.userSeqNo;
    if (!seqNo) return;

    const param: HelpCenterEditParams = {
      seqNo,
      content: this.editContent,
      isIT: this.isIT,
      isReleased: false
    };

    this.persist(param, 'HelpCenter.Message.SaveSuccess');
  }

  saveRelease(): void {
    const seqNo = this.isIT ? this.editData?.itSeqNo : this.editData?.userSeqNo;
    if (!seqNo) return;

    const param: HelpCenterEditParams = {
      seqNo,
      content: this.editContent,
      isIT: this.isIT,
      isReleased: true
    };

    this.persist(param, 'HelpCenter.Message.PublishSuccess');
  }

  private persist(param: HelpCenterEditParams, successI18nKey: string): void {
    this.loading = true;
    this.helpCenterService.updateGuideContent(param)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('Common.Toast.Success'),
            detail: this.translateService.instant(successI18nKey)
          });
          this.loadData();
          this.isEditing = false;
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('Common.Toast.Error'),
            detail: this.translateService.instant('HelpCenter.Message.OperationFailed')
          });
        }
      });
  }

  /**
   * 檢查是否有未儲存的變更
   */
  hasUnsavedChanges(): boolean {
    // 比較當前編輯內容與原始內容
    const originalContent = this.draftContent || this.releasedContent;
    return this.editContent !== originalContent;
  }

  /**
   * 路由守衛會呼叫此方法
   */
  canDeactivate(): boolean {
    // 如果不在編輯模式或沒有未儲存的變更，直接允許離開
    if (!this.isEditing || !this.hasUnsavedChanges()) {
      return true;
    }

    // 有未儲存的變更時，返回 false 會被路由守衛攔截
    // 實際的確認對話框會由守衛處理
    return false;
  }
}
